import API from "../../api/api";
import moment from "moment";
import { getPathologyEventId } from "../../utilities/tables-event-type";

const $API = new API();

let _pendingPollTimers = [];

const PENDING_STORAGE_KEY = "hmc-pending-summaries";

/**
 * How long a "summary in progress" marker survives a page reload before the
 * row offers "צור" again. Long enough to cover a working day so a doctor
 * cannot create the same summary twice, short enough that a creation that
 * never produced a document is not stuck forever.
 */
const PENDING_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * Cellosign delivers the signed summary asynchronously, so the document shows
 * up in /get-documents some time after the doctor returns to the table.
 * Delays are measured from the moment the poll is armed.
 */
const PENDING_POLL_DELAYS = [15000, 30000, 60000, 120000, 300000];

/** @returns {{id: String, ts: Number}[]} non-expired markers from storage */
function readPendingSummaries() {
  try {
    const raw = JSON.parse(localStorage.getItem(PENDING_STORAGE_KEY));
    if (!Array.isArray(raw)) return [];
    const now = Date.now();
    return raw.filter(p => p && p.id && now - p.ts < PENDING_TTL_MS);
  } catch (e) {
    return [];
  }
}

function writePendingSummaries(entries) {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(entries));
  } catch (e) {
    /// Storage unavailable (private mode / quota) - keep the in-memory state.
  }
}

/** The results summary document, as opposed to the pathology report itself. */
function isPathologySummaryDoc(ev) {
  if (!ev || !ev.file) return false;
  const type = ev.file.type || "";
  const description = ev.file.description || "";
  const path = ev.file.path || "";
  return (
    type.includes("pathological-summary") ||
    description === "סיכום תוצאות פתולוגיה" ||
    path.includes("-70.")
  );
}

/**
 * Newest first. A doctor who created the summary twice should see the last
 * one; the API order is not guaranteed, so sort explicitly.
 */
function byNewestDocument(a, b) {
  const dateA = a.document_datetime?.document_datetime || "";
  const dateB = b.document_datetime?.document_datetime || "";
  if (dateA !== dateB) return dateA < dateB ? 1 : -1;
  return (b.DT_RowId || 0) - (a.DT_RowId || 0);
}

const TableDocs = {
  state: () => ({
    docsEvents: [],
    pendingSummaries: readPendingSummaries(),
    docsTypes: {
      pathology: ["pathological-report"],
      genetics: ["pgd-lab-results", "dnachip-lab-results", "exome-lab-results"]
    }
  }),
  getters: {
    docsTypes(state) {
      return state.docsTypes;
    },
    pendingSummaryIds(state) {
      return state.pendingSummaries.map(p => p.id);
    },
    defaultDates() {
      return {
        startDate: moment(new Date())
          .subtract(3, "week")
          .format("YYYY-MM-DDT00:00:00"),
        endDate: moment().format("YYYY-MM-DDT00:00:00")
      };
    },
    eventsByType(state) {
      const events = {};
      if (!state.docsEvents?.length) return events;

      for (const key in state.docsTypes) {
        events[key] = state.docsEvents.filter(
          e =>
            e.file &&
            state.docsTypes[key].some(
              type =>
                (e.file.path || "").includes(type) ||
                (e.file.type || "").includes(type)
            )
        );
      }
      return events;
    },
    hasUnwatchedDocs: (state, getters) => (type = "pathology") => {
      if (!getters.eventsByType[type]) return false;
      return getters.eventsByType[type].some(e => e.count == 0);
    }
  },
  mutations: {
    ADD_PENDING_SUMMARY(state, id) {
      if (!id || state.pendingSummaries.some(p => p.id === id)) return;
      state.pendingSummaries.push({ id, ts: Date.now() });
      writePendingSummaries(state.pendingSummaries);
    },
    REMOVE_PENDING_SUMMARY(state, id) {
      const idx = state.pendingSummaries.findIndex(p => p.id === id);
      if (idx === -1) return;
      state.pendingSummaries.splice(idx, 1);
      writePendingSummaries(state.pendingSummaries);
    },
    SET_DOCS_LIST(state, events) {
      state.docsEvents = events.map(e => {
        const id = e.file && e.file.path ? e.file.path : "";
        const event_id = getPathologyEventId(e);
        const count = e.doctor_view_count
          ? e.doctor_view_count.doctor_view_count
          : 0;
        const is_available_online =
          e?.document_status?.document_status === "פעיל וזמין למטופל"
            ? "זמין"
            : "לא זמין";
        let located_report = "לא אותר";
        let has_pathology_summary = false;
        let pathology_summary_path = "";
        let pathology_summary_file = "";
        // Pathology report rows: locate the matching results summary document
        const patientId = e.real_patient?.real_patient_id;
        const isPathologyReport =
          e.file &&
          state.docsTypes.pathology.some(
            type =>
              (e.file.path || "").includes(type) ||
              (e.file.type || "").includes(type)
          );
        if (isPathologyReport) {
          const summaries = events
            .filter(ev => ev !== e && isPathologySummaryDoc(ev))
            .sort(byNewestDocument);
          /// `event_name` ("<type>_<id>") is the dependable link: summaries
          /// created through Cellosign come back with an empty
          /// `real_patient_id`, so matching on the patient alone misses them.
          /// The patient match stays as a fallback for documents that carry no
          /// event name.
          const foundReport =
            (event_id &&
              summaries.find(ev => getPathologyEventId(ev) === event_id)) ||
            (patientId &&
              summaries.find(
                ev => ev.real_patient?.real_patient_id === patientId
              ));
          if (foundReport) {
            const viewCount = foundReport.view_count?.view_count;
            located_report =
              viewCount && parseInt(viewCount) > 0 ? "צפה" : "לא צפה";
            if (foundReport.file?.path) {
              has_pathology_summary = true;
              pathology_summary_path =
                "https://md-booking.hmc.co.il/ex-doctor/external" +
                foundReport.file.path;
              pathology_summary_file =
                foundReport.file.file_name ||
                foundReport.file.description ||
                "סיכום תוצאות פתולוגיה";
            }
          }
        }
        return {
          ...e,
          id,
          event_id,
          count,
          located_report,
          is_available_online,
          has_pathology_summary,
          pathology_summary_path,
          pathology_summary_file
        };
      });

      /// Drop "in progress" markers whose summary has arrived, plus any that
      /// outlived the TTL. Keyed by event id - the same key the table row uses.
      const resolved = new Set(
        state.docsEvents
          .filter(ev => ev.has_pathology_summary && ev.event_id)
          .map(ev => ev.event_id)
      );
      const now = Date.now();
      state.pendingSummaries = state.pendingSummaries.filter(
        p => !resolved.has(p.id) && now - p.ts < PENDING_TTL_MS
      );
      writePendingSummaries(state.pendingSummaries);
    },
    UPDATE_FILE_WATCHED(state, filePath) {
      const path = filePath.replace(
        "https://md-booking.hmc.co.il/ex-doctor/external",
        ""
      );
      const i = state.docsEvents.findIndex(e => e.id == path);
      if (i === -1) return;
      state.docsEvents[i].count++;
    }
  },
  actions: {
    async GET_DOCS_LIST({ commit, getters, rootGetters }, newDates) {
      const doctorId = rootGetters.activeDoctor.id;
      const dates = newDates ?? getters.defaultDates;

      if (!doctorId) return [];
      const res = await $API.getTableDocsList({
        doctorId,
        fromDate: moment(dates.startDate, "YYYY-MM-DDThh:mm:ss").format(
          "YYYY-MM-DD"
        ),
        toDate: moment(dates.endDate, "YYYY-MM-DDThh:mm:ss").format(
          "YYYY-MM-DD"
        )
      });
      if (!res || !res.data || !res.data.data) return [];

      commit("SET_DOCS_LIST", res.data.data);
      return res.data.data;
    },
    POLL_PENDING_SUMMARIES({ state, dispatch }, dates) {
      _pendingPollTimers.forEach(t => clearTimeout(t));
      _pendingPollTimers = [];

      if (!state.pendingSummaries.length) return;

      PENDING_POLL_DELAYS.forEach(delay => {
        const timer = setTimeout(async () => {
          if (!state.pendingSummaries.length) return;
          try {
            await dispatch("GET_DOCS_LIST", dates);
          } catch (e) {
            /// A failed refresh should not break the remaining retries.
          }
        }, delay);
        _pendingPollTimers.push(timer);
      });
    },
    CANCEL_PENDING_POLL() {
      _pendingPollTimers.forEach(t => clearTimeout(t));
      _pendingPollTimers = [];
    }
  }
};

export default TableDocs;
